import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiLock, FiDelete, FiArrowLeft } from 'react-icons/fi';
import '../styles/SetPinPage.css';

const SetPinPage = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNumberClick = (number) => {
    if (step === 1) {
      if (pin.length < 4) {
        setPin(prev => prev + number);
        if (pin.length + 1 === 4) {
          setTimeout(() => setStep(2), 300);
        }
      }
    } else {
      if (confirmPin.length < 4) {
        setConfirmPin(prev => prev + number);
      }
    }
  };

  const handleDelete = () => {
    if (step === 1) {
      setPin(prev => prev.slice(0, -1));
    } else {
      setConfirmPin(prev => prev.slice(0, -1));
    }
  };

  const handlePinConfirm = async () => {
    if (pin === confirmPin) {
      try {
        setLoading(true);
        localStorage.setItem('walletPin', pin);
        navigate('/loading');
      } catch (error) {
        setError('Failed to set PIN');
        setStep(1);
        setPin('');
        setConfirmPin('');
      } finally {
        setLoading(false);
      }
    } else {
      setError('PINs do not match');
      setTimeout(() => {
        setStep(1);
        setPin('');
        setConfirmPin('');
        setError('');
      }, 1500);
    }
  };

  useEffect(() => {
    if (confirmPin.length === 4) {
      handlePinConfirm();
    }
  }, [confirmPin]);

  const currentPin = step === 1 ? pin : confirmPin;

  return (
    <div className="page-container">
      <div className="aurora-bg">
        <div className="aurora-blob aurora-1" />
        <div className="aurora-blob aurora-2" />
        <div className="grid-overlay" />
      </div>

      <motion.div
        className="content-wrapper centered-content"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-nav">
          {step === 2 && (
            <button className="nav-back-btn" onClick={() => { setStep(1); setConfirmPin(''); }}>
              <FiArrowLeft />
            </button>
          )}
        </div>

        <div className="glass-card pin-setup-card">
          <div className="lock-avatar">
            <FiLock />
          </div>

          <div className="text-center mb-8">
            <motion.h1
              key={step}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl font-bold mb-2 text-white"
            >
              {step === 1 ? 'Create PIN' : 'Confirm PIN'}
            </motion.h1>
            <motion.p
              key={`desc-${step}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-secondary"
            >
              {step === 1 ? 'Secure your wallet with a 4-digit PIN' : 'Re-enter your PIN to verify'}
            </motion.p>
          </div>

          <div className="pin-dots-container">
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={i}
                className={`pin-dot-indicator ${currentPin.length > i ? 'active' : ''}`}
                animate={{
                  scale: currentPin.length > i ? 1.1 : 1,
                  backgroundColor: currentPin.length > i ? '#2b5aff' : 'rgba(255,255,255,0.1)'
                }}
              />
            ))}
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                className="error-toast"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="keypad-grid mt-8">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                className="keypad-btn"
                onClick={() => handleNumberClick(num)}
              >
                {num}
              </button>
            ))}
            <div className="keypad-btn empty" />
            <button className="keypad-btn" onClick={() => handleNumberClick(0)}>0</button>
            <button className="keypad-btn delete" onClick={handleDelete}>
              <FiDelete />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SetPinPage;
